import { FC, KeyboardEvent, MouseEvent } from 'react';
import { Button, Menu, Portal } from '@chakra-ui/react';
import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { useToastAlert } from '../../../hooks/useToastAlert/useToastAlert';
import { ITrainingBlock } from '../../../models/allenamenti';
import { useBlockClipboard } from './useBlockClipboard';
import styles from './BlockCard.module.scss';

interface IBlockCardMenuProps {
  block: ITrainingBlock;
}

/** Ferma qui sia il click che il tasto premuto: non devono risalire alla card. */
const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();

/**
 * Kebab della card (F1): oggi la sola voce "Copia". Stesso pattern Chakra
 * `Menu.Root`/`Menu.Trigger`/`Portal` già usato in TeamCard/CalendarPage.
 *
 * Vive in un componente a parte perché la card ospitante è essa stessa
 * cliccabile/trascinabile (apre la modal, o parte un drag da tastiera con
 * Spazio): un click o uno Spazio dato QUI non deve MAI propagarsi fin là,
 * altrimenti "Copia" aprirebbe anche la modal di modifica. Gli `onClick` dei
 * `Menu.Item`, pur renderizzati in un `Portal` (altrove nel DOM), risalgono
 * comunque l'albero REACT — non quello DOM — fino a qui: fermarli sul
 * contenitore che avvolge l'intero `Menu.Root` intercetta sia il trigger sia
 * le voci del menu con un solo `stopPropagation`.
 */
const BlockCardMenu: FC<IBlockCardMenuProps> = ({ block }) => {
  const { copy } = useBlockClipboard();
  const toastAlert = useToastAlert();

  /**
   * Copia + feedback: ogni azione deve avere una reazione visibile, altrimenti
   * chi clicca "Copia" non ha modo di sapere che è servita a qualcosa finché
   * non prova a incollare altrove.
   */
  const handleCopy = () => {
    copy(block);
    toastAlert({ message: 'Blocco copiato' });
  };

  return (
    <span className={styles.menuWrap} onClick={stop} onKeyDown={stop}>
      <Menu.Root>
        <Menu.Trigger asChild>
          <Button
            variant="ghost"
            size="xs"
            className={styles.kebab}
            aria-label="Azioni blocco"
            data-testid="block-card-kebab"
          >
            <DotsThreeVerticalIcon size={16} weight="bold" />
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content className="ps-2 pe-2">
              <Menu.Item
                value="copy"
                data-testid="block-menu-copy"
                onClick={handleCopy}
              >
                Copia
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </span>
  );
};

export default BlockCardMenu;
